<template>
  <Modal
    :show="isVisible"
    :title="modalTitle"
    :save-label="t('Salvar')"
    :close-label="t('Fechar')"
    :save-fn="handleSave"
    :save-disabled-fn="isSaveDisabled"
    size="large"
    elem-id="modalFranquia"
    @close="closeModal"
    @dismiss="closeModal"
    >
      
    <!-- Content only renders when modal is visible (prevents API calls on mount) -->
    <template v-if="isVisible">
      <Tabs v-model:value="activeTabIndex">
        <TabList>
          <Tab :value="0">{{ t('Informações') }}</Tab>
          <Tab v-if="isEditMode" :value="1">{{ t('Áreas de atuação') }}</Tab>
          <Tab v-if="isEditMode" :value="2">{{ t('Configurações') }}</Tab>
        </TabList>
        
        <TabPanels>
          <TabPanel :value="0">
            <franquias-form
              ref="formRef"
              :franquia-id="currentFranquiaId"
              :is-edit-mode="isEditMode"
              @form-valid="handleFormValidChange">
            </franquias-form>
          </TabPanel>

          <TabPanel v-if="isEditMode" :value="1">
            <franquias-locations-tab
              v-if="currentFranquiaId"
              ref="locationsTabRef"
              :franquia-id="currentFranquiaId"
              :franquia-data="franquiaData">
            </franquias-locations-tab>
          </TabPanel>

          <TabPanel v-if="isEditMode" :value="2">
            <franquias-config-tab
              v-if="currentFranquiaId"
              ref="configTabRef"
              :franquia-id="currentFranquiaId">
            </franquias-config-tab>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { log } from '../../utils/DebugLogger';
import { useFranquias } from '../../composables/useFranquias';
import { permissionsService } from '../../services/permissionsService';
import FranquiasForm from './FranquiasForm.vue';
import FranquiasLocationsTab from './FranquiasLocationsTab.vue';
import FranquiasConfigTab from './FranquiasConfigTab.vue';
import type { Franquias_SalvaFranquiaInput } from '../../contracts/Franquias_SalvaFranquiaInput';

type Id = number | string;

interface FranquiaInner {
  Nome?: string;
  [key: string]: unknown;
}

interface FranquiaResponse {
  Franquia?: FranquiaInner;
  [key: string]: unknown;
}

interface FormInstance {
  loadData: (data: FranquiaInner | undefined) => void;
  getFormData: () => Franquias_SalvaFranquiaInput;
}

interface ModalOpenPayload {
  id?: Id;
  action?: string;
}

const { t } = useI18n();
const { fetchFranquia, saveFranquia } = useFranquias();

// Emits
const emit = defineEmits<{
  saved: [];
  close: [];
}>();

// Reactive data
const isVisible = ref<boolean>(false);
const isEditMode = ref<boolean>(false);
const currentFranquiaId = ref<number | undefined>(undefined);
const activeTabIndex = ref<number>(0);
const isFormValid = ref<boolean>(false);
const franquiaData = ref<FranquiaResponse | undefined>(undefined);

// Refs
const formRef = ref<FormInstance | null>(null);
const locationsTabRef = ref<unknown>(null);
const configTabRef = ref<unknown>(null);

// Computed
const modalTitle = computed(() => {
  if (isEditMode.value && franquiaData.value && franquiaData.value.Franquia) {
    return franquiaData.value.Franquia.Nome || t('Editar Franquia');
  }
  return t('Nova Franquia');
});

const isSaveDisabled = (): boolean => {
  // Only validate on the informacoes tab (index 0)
  if (activeTabIndex.value === 0) {
    return !isFormValid.value;
  }
  // Other tabs don't have save buttons in the modal footer
  return true;
};

// Methods
const openForCreate = (): void => {
  log('FranquiasModal', 'openForCreate', 'Opening modal for create');
  isEditMode.value = false;
  currentFranquiaId.value = undefined;
  franquiaData.value = undefined;
  activeTabIndex.value = 0;
  isVisible.value = true;
};

const openForEdit = async (franquiaId: Id): Promise<void> => {
  log('FranquiasModal', 'openForEdit', 'Opening modal for edit', franquiaId);
  isEditMode.value = true;
  currentFranquiaId.value = Number(franquiaId);
  activeTabIndex.value = 0;
  isVisible.value = true;

  // Load franchise data
  try {
    franquiaData.value = await fetchFranquia(Number(franquiaId)) as FranquiaResponse;
    log('FranquiasModal', 'openForEdit', 'Franchise data loaded', franquiaData.value);

    // Wait for the form component to be rendered
    await nextTick();

    // Load form data
    if (formRef.value) {
      formRef.value.loadData(franquiaData.value?.Franquia);
    }
  } catch (error) {
    log('FranquiasModal', 'openForEdit', 'Error loading franchise', error);
    eventBus.emit('globalMessage.error', t('Erro ao carregar franquia'));
    closeModal();
  }
};

// Standard modal open handler (for modal.franquia.open event)
const handleModalOpen = (data?: ModalOpenPayload): void => {
  log('FranquiasModal', 'handleModalOpen', 'Standard modal open event received', data);

  if (data?.id) {
    // Edit mode
    openForEdit(data.id);
  } else {
    // Simple open (new)
    openForCreate();
  }
};

const closeModal = (): void => {
  log('FranquiasModal', 'closeModal', 'Closing modal');
  isVisible.value = false;
  isEditMode.value = false;
  currentFranquiaId.value = undefined;
  franquiaData.value = undefined;
  activeTabIndex.value = 0;
  isFormValid.value = false;
};

const handleSave = async (): Promise<void> => {
  // Only save if we're on the informacoes tab
  if (activeTabIndex.value !== 0) {
    return;
  }

  log('FranquiasModal', 'handleSave', 'Saving franchise');

  if (!formRef.value) {
    log('FranquiasModal', 'handleSave', 'Form ref not available');
    return;
  }

  try {
    const formData = formRef.value.getFormData();
    log('FranquiasModal', 'handleSave', 'Form data', formData);

    await saveFranquia(formData);

    // Refresh permissions to include the new franchise in the user's accessible franchises
    log('FranquiasModal', 'handleSave', 'Refreshing permissions to include new franchise');
    await permissionsService.refreshPermissions();

    eventBus.emit('globalMessage.success', t('Franquia salva com sucesso'));
    eventBus.emit('franquias.refresh');
    eventBus.emit('filter.updated'); // Notify FranchiseSelect to reload

    emit('saved');
    closeModal();
  } catch (error) {
    log('FranquiasModal', 'handleSave', 'Error saving franchise', error);
    eventBus.emit('globalMessage.error', t('Erro ao salvar franquia'));
  }
};

const handleFormValidChange = (isValid: boolean): void => {
  isFormValid.value = isValid;
};

// Handler for refreshing franchise data (from locations tab)
const handleRefreshSingle = async (franquiaId?: Id): Promise<void> => {
  // Only refresh if it's for this franchise
  if (franquiaId === currentFranquiaId.value) {
    log('FranquiasModal', 'handleRefreshSingle', 'Refreshing franchise data', franquiaId);
    try {
      franquiaData.value = await fetchFranquia(Number(franquiaId)) as FranquiaResponse;
      log('FranquiasModal', 'handleRefreshSingle', 'Franchise data refreshed', franquiaData.value);
    } catch (error) {
      log('FranquiasModal', 'handleRefreshSingle', 'Error refreshing franchise', error);
    }
  }
};

// Sync wrappers for async eventBus handlers (EventCallback must return void)
const handleModalOpenSync = (data?: ModalOpenPayload): void => {
  handleModalOpen(data);
};
const handleRefreshSingleSync = (franquiaId?: Id): void => {
  void handleRefreshSingle(franquiaId);
};

// Event listeners
onMounted(() => {
  log('FranquiasModal', 'onMounted', 'Component mounted');

  // Standard modal events (preferred pattern)
  eventBus.on('modal.franquia.open', handleModalOpenSync);
  eventBus.on('modal.franquia.close', closeModal);

  // Listen for franchise data refresh requests (from locations tab)
  eventBus.on('franquias.refresh-single', handleRefreshSingleSync);
});

onBeforeUnmount(() => {
  log('FranquiasModal', 'onBeforeUnmount', 'Component unmounting');

  // Remove standard modal events
  eventBus.off('modal.franquia.open', handleModalOpenSync);
  eventBus.off('modal.franquia.close', closeModal);

  // Remove refresh listener
  eventBus.off('franquias.refresh-single', handleRefreshSingleSync);
});

// Expose methods for parent
defineExpose({
  openForCreate,
  openForEdit,
  closeModal,
});
</script>

<style scoped>
/* PrimeVue TabView handles styling */
</style>
